import { Component, inject, signal } from '@angular/core';
import { PackageArea, PackageExtended, PackageZone } from '../../models/package';
import { AutoSizeStrategy, ColDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { IconButtonCellRenderer } from '../icon-button-cell-renderer/icon-button-cell-renderer';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { BackendApi } from '../../services/backend-api';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';
import { MatExpansionModule } from '@angular/material/expansion';
import { CreatePackageAreaDialog } from '../create-package-area-dialog/create-package-area-dialog';
import { CreatePackageZoneDialog } from '../create-package-zone-dialog/create-package-zone-dialog';
import { MatIconModule } from '@angular/material/icon';
import { UpdatePackageDialog } from '../update-package-dialog/update-package-dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-show-package',
  imports: [AgGridAngular, MatExpansionModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './show-package.html',
  styleUrl: './show-package.css',
})
export class ShowPackage {
  private _activatedRoute = inject(ActivatedRoute);
  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);

  public package = signal<PackageExtended|undefined>(undefined);

  public autoSizeStrategy: AutoSizeStrategy = {
    type: 'fitGridWidth',
    defaultMinWidth: 100,
  };

  public localeText = AG_GRID_LOCALE_FR;

  public areaColumns: ColDef<PackageArea>[] = [
    { field: "area.country.flag", headerName: "", filter: false, sortable: false, width: 50 },
    { field: "area.country.name_fr", headerName: "Pays", filter: true, sortable: true, floatingFilter: true },
    { field: "area.is_landline", cellDataType: false, headerName: "Fixe / Mobile", valueFormatter: (params) => params.value ? 'Fixe' : 'Mobile' },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette entrée',
        onClick: (data: any, index: number) => this.deletePackageArea(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  public zoneColumns: ColDef<PackageZone>[] = [
    { field: "zone", headerName: "Zone", filter: true, sortable: true, floatingFilter: true },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette entrée',
        onClick: (data: any, index: number) => this.deletePackageZone(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  private areaGridApi!: GridApi;
  private zoneGridApi!: GridApi;

  constructor(
    private readonly backendApi: BackendApi,
    private readonly dialog: MatDialog
  ) {
    this._activatedRoute.params.subscribe((params) => {
      this.backendApi.getPackage(params['uuid']).subscribe({
        next: (thePackage) => {
          this.package.set(thePackage);
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la recuperation du forfait.");
        }
      })
    });
  }

  onAreaGridReady(event: GridReadyEvent) {
    this.areaGridApi = event.api;
  }

  onZoneGridReady(event: GridReadyEvent) {
    this.zoneGridApi = event.api;
  }

  getRowId(params: GetRowIdParams): string {
    return params.data.uuid;
  }

  createPackageArea() {
    const dialogRef = this.dialog.open(CreatePackageAreaDialog, {
      data: this.package(),
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((packageArea) => {
      if (packageArea) {
        this.areaGridApi.applyTransaction({
          add: [packageArea]
        });
      }
    });
  }

  createPackageZone() {
    const dialogRef = this.dialog.open(CreatePackageZoneDialog, {
      data: this.package(),
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((packageZone) => {
      if (packageZone) {
        this.zoneGridApi.applyTransaction({
          add: [packageZone]
        });
      }
    });
  }

  deletePackageArea(packageArea: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette entrée ?"},
    });
  
    dialogRef.afterClosed().subscribe(result => {
      const packageUuid = this.package()?.uuid;

      if (!result || !packageUuid) {
        return;
      }

      this.backendApi.deletePackageArea(packageUuid, packageArea.uuid).subscribe({
        next: () => {
          this.areaGridApi.applyTransaction({remove: [packageArea]});
          this._snackBar.open("Entrée supprimée.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }

  deletePackageZone(packageZone: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette entrée ?"},
    });
  
    dialogRef.afterClosed().subscribe(result => {
      const packageUuid = this.package()?.uuid;

      if (!result || !packageUuid) {
        return;
      }

      this.backendApi.deletePackageZone(packageUuid, packageZone.uuid).subscribe({
        next: () => {
          this.zoneGridApi.applyTransaction({remove: [packageZone]});
          this._snackBar.open("Entrée supprimée.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }

  updatePackage() {
    if (!this.package()) {
      return;
    }

    const dialogRef = this.dialog.open(UpdatePackageDialog, {
      width: '400px',
      data: this.package()
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.package.set(result);
    });
  }

  deletePackage() {
    const packageUuid = this.package()?.uuid;

    if (!packageUuid) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer ce forfait ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.backendApi.deletePackage(packageUuid).subscribe({
        next: () => {
          this._snackBar.open("Forfait supprimé.");
          this._router.navigate(["/packages"]);
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }
}
